/* eslint-disable react/jsx-indent */
// jsx-indent disabled: the shell is wrapped in <ProjectModeProvider> (keeps you
// "inside" a project across its doc sub-views); the wrapper shifts nesting but the
// tree keeps its original indent for a readable diff.
import React, { Suspense, useEffect, useRef } from 'react';
import {
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import './styles/global.css';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box, Button, Center, ChakraProvider, Heading, Spinner, Text, VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import ProposalsPage from './pages/ProposalsPage';
import HandOffsPage from './pages/HandOffsPage';
import ProposalForm from './shared/forms/ProposalForm';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import WorkOrderForm from './shared/forms/WorkOrderForm';
import WorkOrdersPage from './pages/WorkOrdersPage';
import PurchaseOrderForm from './shared/forms/PurchaseOrderForm';
import DashboardPage from './pages/DashboardPage';
import SidebarWithHeader from './shared/SidebarWithHeader';
import RecurringRevenuePage from './pages/RecurringRevenuePage';
import buildBrandTheme from './theme/brandTheme';
import BrandThemeProvider from './theme/BrandThemeProvider';
import { BrandSourceProvider } from './theme/BrandSourceProvider';
import { SkinProvider } from './theme/SkinProvider';
import { AppearanceProvider } from './theme/AppearanceProvider';
import { shouldRenderApp, shouldRedirectToLogin, shouldShowLoginFailed } from './auth/authGate';
import useCurrentUser from './hooks/useCurrentUser';
import PortalThemeProvider from './portal/PortalThemeProvider'; // item 852 — the portal's own (RNWY) brand
import OutsideSalesThemeProvider from './portal/OutsideSalesThemeProvider'; // item 855 — forced company brand
import { useHeartbeat } from './hooks/usePresence';
import { PinLockProvider } from './theme/PinLockProvider';
import PinLockScreen from './shared/PinLockScreen';
import ErrorBoundary from './shared/ErrorBoundary';
import KpiStrip from './shared/dashboard/KpiStrip';
import DashboardBentoSlot from './shared/dashboard/DashboardBentoSlot';
import { DashboardExpandProvider } from './shared/dashboard/DashboardExpandContext';
import GuidedTour from './shared/GuidedTour';
import ProjectContextBar from './shared/dashboard/ProjectContextBar';
import CommandPalette from './shared/CommandPalette';
import KeyboardShortcuts from './shared/KeyboardShortcuts';
import { ProjectModeProvider } from './theme/ProjectModeProvider';
import ConfirmProvider from './shared/ConfirmProvider';
import { readBrandCache } from './hooks/useCompanyBrand';
import FirstLoginBrandWizard from './shared/FirstLoginBrandWizard';
import ProjectForm from './shared/forms/ProjectForm';
import ProjectPage from './pages/ProjectPage';
import InvoicePage from './pages/InvoicePage';
import InvoiceForm from "./shared/forms/InvoiceForm";
import BillPage from "./pages/BillPage";
import BillForm from "./shared/forms/BillForm";
import {
  getImpersonatedRoleName, getImpersonatedCustomerId, getImpersonatedUserId, stopImpersonation,
  IMPERSONATE_HEADER, IMPERSONATE_CUSTOMER_HEADER, IMPERSONATE_USER_HEADER,
} from './utils/impersonation'; // item 323/843/851
import ImpersonationBanner from './shared/ImpersonationBanner'; // item 323
import StealthIndicator from './shared/StealthIndicator'; // item 327
import LiveDataPulse from './shared/dashboard/LiveDataPulse'; // item 326
import LayerPanel from './shared/LayerPanel'; // item 455
import { BreadcrumbsProvider } from './shared/BreadcrumbsProvider'; // item 456
import { HeaderBreadcrumbs } from './shared/FormBreadcrumbs'; // item 456
import lazyWithRetry from './utils/lazyWithRetry'; // item 1515
import MaintenanceBanner from './shared/MaintenanceBanner'; // item 1576
import useNewVersionCheck from './hooks/useNewVersionCheck'; // item 1502
import { installClientErrorReporter } from './utils/clientErrorReporter'; // item 1525-web

// item 1515 — CODE SPLITTING. The dashboard + the five document grids/forms (the daily path) stay
// eager above; every layer-2 tool panel, both external portals and the anonymous /public/* pages
// load as their own chunks on first visit (lazyWithRetry self-heals a stale tab's chunk 404 with
// ONE reload). The Suspense boundaries sit INSIDE the auth + role gates, around the route switches.
const SettingsPage = lazyWithRetry(() => import(/* webpackChunkName: "settings" */ './pages/SettingsPage'));
const UsersPage = lazyWithRetry(() => import(/* webpackChunkName: "users" */ './pages/UsersPage'));
const UserForm = lazyWithRetry(() => import(/* webpackChunkName: "users" */ './shared/forms/UserForm'));
const MyBookOfBusinessPage = lazyWithRetry(() => import(/* webpackChunkName: "my-book" */ './pages/MyBookOfBusinessPage'));
const MyTimePage = lazyWithRetry(() => import(/* webpackChunkName: "my-time" */ './pages/MyTimePage')); // item 1337b — Tools → My Time
const ReportsPage = lazyWithRetry(() => import(/* webpackChunkName: "reports" */ './pages/ReportsPage'));
const DepartmentTasksPage = lazyWithRetry(() => import(/* webpackChunkName: "department-tasks" */ './pages/DepartmentTasksPage')); // item 274
const AccountantPage = lazyWithRetry(() => import(/* webpackChunkName: "accountant" */ './pages/AccountantPage'));
const ItemsPage = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/ItemsPage'));
const ItemForm = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './shared/forms/ItemForm'));
const ContactPage = lazyWithRetry(() => import(/* webpackChunkName: "customers" */ './pages/ContactPage'));
const ContactForm = lazyWithRetry(() => import(/* webpackChunkName: "customers" */ './shared/forms/ContactForm'));
const AppearancePage = lazyWithRetry(() => import(/* webpackChunkName: "appearance" */ './pages/AppearancePage'));
const SalesBibleFocusPage = lazyWithRetry(() => import(/* webpackChunkName: "sales-bible" */ './pages/SalesBibleFocusPage'));
const RnwyFleetPage = lazyWithRetry(() => import(/* webpackChunkName: "rnwy-fleet" */ './pages/RnwyFleetPage')); // item 861 — live RNWY capacity fleet
const CustomerRequestsPage = lazyWithRetry(() => import(/* webpackChunkName: "customer-requests" */ './pages/CustomerRequestsPage')); // item 868a — the pricing queue
const LiveDashboardPage = lazyWithRetry(() => import(/* webpackChunkName: "live-dashboard" */ './pages/LiveDashboardPage')); // item 326
const MailboxPage = lazyWithRetry(() => import(/* webpackChunkName: "mailbox" */ './pages/MailboxPage')); // item 354 — shared company inbox
const ChatFocusPage = lazyWithRetry(() => import(/* webpackChunkName: "chat" */ './pages/ChatFocusPage')); // item 395 — chat focus/undock
const CustomerPortalApp = lazyWithRetry(() => import(/* webpackChunkName: "customer-portal" */ './portal/CustomerPortalApp')); // item 843 — external Customer Builder portal
const OutsideSalesPortalApp = lazyWithRetry(() => import(/* webpackChunkName: "sales-portal" */ './portal/OutsideSalesPortalApp')); // item 855 — Sales I/O rep portal
const PublicProposalPage = lazyWithRetry(() => import(/* webpackChunkName: "public-proposal" */ './pages/PublicProposalPage')); // item 227 — anonymous public route
const PublicEeulaPage = lazyWithRetry(() => import(/* webpackChunkName: "public-eeula" */ './pages/PublicEeulaPage')); // item 978 — anonymous EEULA clickwrap
const PublicAutoPayPage = lazyWithRetry(() => import(/* webpackChunkName: "public-autopay" */ './pages/PublicAutoPayPage')); // item 882
const PublicCollectionsPage = lazyWithRetry(() => import(/* webpackChunkName: "public-collections" */ './pages/PublicCollectionsPage')); // item 1163d
const PublicPaymentPage = lazyWithRetry(() => import(/* webpackChunkName: "public-pay" */ './pages/PublicPaymentPage')); // item 589 — anonymous payment confirmation

// item 1515 — the same neutral spinner the auth/role gates show, as the Suspense fallback while
// a route chunk downloads (inside the shell it sits in the scroll region, so padded, not 100vh)
const RouteFallback: React.FC<{ fullScreen?: boolean }> = ({ fullScreen }) => (
  <Center h={fullScreen ? '100vh' : undefined} py={fullScreen ? 0 : 24}>
    <Spinner size="xl" color="brandGreen.500" thickness="4px" label="Loading" />
  </Center>
);
RouteFallback.defaultProps = { fullScreen: false };

// item 1502 — the new-version toast watcher: a hook that needs Chakra's toast context, so it
// mounts as a null component INSIDE the theme provider, once per authenticated tree
const NewVersionWatcher: React.FC = () => {
  useNewVersionCheck();
  return null;
};

// boot theme: last cached company brand if we have one, stock Basix otherwise —
// no flash of the wrong brand while the settings request is in flight
const basixTheme = buildBrandTheme(readBrandCache());

// item 455 — the LAYERING model. Layer 0 (the canvas) = Projects + the Basix Six
// documents + the dashboard landing; layer 1 = wizards (Chakra modals, z 1400);
// layer 2 = every tool/settings/dashboard surface below, rendered as a full-screen
// panel that slides up OVER the still-mounted canvas (LayerPanel, z 1250). Closing
// the panel returns to the exact canvas route you were on — nothing unmounted, so
// scroll/form/expansion state all survive. NOT layer 2: /recurring (item 457 made
// subscriptions part of the Basix core under Invoices) and /sales-bible (it runs
// its own full-page focus takeover).
const LAYER2_PREFIXES = [
  '/settings', '/reports', '/chat', '/mailbox', '/accountant',
  '/timesheet', '/ic-invoices', '/department-tasks', '/appearance', '/users', '/user',
  '/items', '/item', '/customers', '/customer', '/my-book', '/my-time', '/dashboard/live',
  '/sales-bible', // item 476 — Sales Bible is a layer-2 panel view now (was a full-screen takeover)
  '/rnwy-fleet', // item 861 — live RNWY system capacity fleet view
  '/customer-requests', // item 868a — the customer-portal pricing queue
];
const isLayer2Path = (p: string) => LAYER2_PREFIXES.some(
  (x) => p === x || p.startsWith(`${x}/`),
);

function App() {
  const {
    isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect, logout, error: authError,
    user: auth0User,
  } = useAuth0();
  const location = useLocation();
  const { pathname } = location;
  const history = useHistory();
  // item 272 — Outside Sales is an external self-service seat: confine routing to their
  // portal (My Book + Appearance); every other route redirects back to My Book.
  // item 594 — Executive is a trusted ops/sales leader with NO money visibility: its financial
  // surfaces (accountant, reports, invoices, bills, POs, subscriptions) are guarded below.
  const {
    isOutsideSales, isExecutive, isCustomerBuilder, isLoading: roleLoading, sessionBroken,
  } = useCurrentUser();
  // item 455 — the layer system: remember the last CANVAS (layer 0) location so a
  // layer-2 route renders as a panel over it. Updated during render (idempotent);
  // /commissions is skipped (it only redirects into /my-book — anchoring there
  // would bounce the close right back into a panel). Outside Sales seats have a
  // two-page portal — no layering for them.
  const layerEnabled = !isOutsideSales;
  const panelOpen = layerEnabled && isLayer2Path(pathname);
  // item 827 (Chad) — the layer's return-to-canvas anchor is PERSISTED (sessionStorage), so a
  // remount while a tool layer is open — a browser refresh, or a dev hot-reload — recovers the
  // page you were on (e.g. the proposal) instead of defaulting to '/' and dumping you on the
  // dashboard when you close the modal. A tool/settings modal must NEVER change what's behind it.
  // item 982 — single-flight guard: a burst of concurrent 401s must trigger ONE
  // login redirect, not one per call (each redirect restarts the OAuth dance and
  // the losers stomp the winner's state).
  const authRedirectingRef = useRef(false);
  const lastCanvasLocRef = useRef<{ pathname: string; search: string }>((() => {
    try {
      const saved = window.sessionStorage.getItem('basix.lastCanvasLoc');
      if (saved) return JSON.parse(saved);
    } catch { /* no storage */ }
    return { pathname: '/', search: '' };
  })());
  if (!isLayer2Path(pathname) && !pathname.startsWith('/commissions')) {
    lastCanvasLocRef.current = { pathname, search: location.search };
    try { window.sessionStorage.setItem('basix.lastCanvasLoc', JSON.stringify(lastCanvasLocRef.current)); } catch { /* no storage */ }
  }
  const canvasLocation = panelOpen ? lastCanvasLocRef.current : location;
  // item 318 — presence heartbeat: while authenticated, ping the server every 45s so
  // teammates see me in the "who's online" strip. Best-effort; mounted once here.
  useHeartbeat(isAuthenticated);
  // item 476 — Sales Bible is a layer-2 panel view now (no canvas chrome suppression).
  // item 326 — the live dashboard is a focus view too: hide the sticky KPI stack
  // (it would double-render the same numbers above the full bento)
  const liveDashFocus = pathname === '/dashboard/live' || pathname === '/chat'; // item 395 — chat focus is chrome-free too
  // item 361 — the old --basix-appheader-h measurement (item 293) is GONE: the KPI
  // stack now renders in the shell's header slot outside the scroll region, so sticky
  // page titles pin at a constant offset and nothing needs measuring.
  useEffect(() => {
    // Fetch the token per request so the Auth0 SDK can serve it from cache and
    // silently renew it on expiry; a token captured once goes stale and every
    // call 401s until a full reload.
    const requestInterceptor = axios.interceptors.request.use(async (req) => {
      // Always attach the token — don't gate on a captured `isAuthenticated`.
      // The app shell is auth-gated (shouldRenderApp), so any request only fires
      // once a session exists. Reading `isAuthenticated` here closed over a stale
      // `false` during the first authenticated render (child query effects run
      // before this parent effect re-registers), so the initial dashboard burst
      // went out tokenless → 401. getAccessTokenSilently serves from cache and
      // renews silently; on genuine failure the response interceptor redirects.
      try {
        const token = await getAccessTokenSilently();
        req.headers.Authorization = `Bearer ${token}`;
      } catch {
        // No usable session — let the 401 response interceptor drive the login.
      }
      // item 323 — role impersonation: tell the backend which role this tab is
      // viewing as. The server re-verifies the caller is a super-admin on every
      // request; for anyone else the header is ignored (and logged).
      const impersonated = getImpersonatedRoleName();
      if (impersonated) req.headers[IMPERSONATE_HEADER] = impersonated;
      // item 843 — CustomerBuilder preview also names the customer (super-admin only, server-verified)
      const impersonatedCustomer = getImpersonatedCustomerId();
      if (impersonatedCustomer) req.headers[IMPERSONATE_CUSTOMER_HEADER] = impersonatedCustomer;
      // item 851 — impersonate a specific user (super-admin only, server-verified)
      const impersonatedUser = getImpersonatedUserId();
      if (impersonatedUser) req.headers[IMPERSONATE_USER_HEADER] = impersonatedUser;
      return req;
    });
    // item 982 — a 401 must NOT dump the user to the Auth0 login mid-work (Tom was
    // kicked out two days running). A 401 usually means the cached access token
    // expired and the silent renewal hiccuped (laptop wake, transient network, the
    // renewal iframe) — so force ONE fresh renewal (ignoreCache bypasses the stale
    // cache entry) and replay the failed request. Only when renewal itself proves
    // impossible (the Auth0 session is genuinely gone) do we redirect to login —
    // and at most once, not once per in-flight call in the burst.
    const responseInterceptor = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const cfg = error?.config as (typeof error.config & { basixRetried?: boolean }) | undefined;
        if (error?.response?.status === 401) {
          if (cfg && !cfg.basixRetried) {
            cfg.basixRetried = true;
            try {
              const token = await getAccessTokenSilently({ ignoreCache: true });
              cfg.headers = { ...cfg.headers, Authorization: `Bearer ${token}` };
              // not awaited: a failed replay rejects to the original caller (and a
              // second 401 re-enters this interceptor with basixRetried already set)
              return axios(cfg);
            } catch {
              // fall through — the session is really unrecoverable
            }
          }
          if (!authRedirectingRef.current) {
            authRedirectingRef.current = true;
            // item 1458 — keep ?query and #anchor so a deep link (e.g. a staff row) lands
            loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search + window.location.hash } });
          }
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [getAccessTokenSilently, loginWithRedirect]);

  // item 1525-web — the client error reporter posts through the app's axios (bearer via the
  // interceptor above), so it installs once a session exists; production only, idempotent.
  useEffect(() => {
    if (isAuthenticated) installClientErrorReporter();
  }, [isAuthenticated]);

  // item 227 — /public/* is the ONE anonymous surface (the client proposal view). It must
  // render WITHOUT a session and must NOT bounce the visitor to Auth0. Everything else stays
  // fully auth-gated below — this only carves out the explicitly-public path.
  const isPublicRoute = pathname.startsWith('/public/');

  useEffect(() => {
    // unauthenticated visits go straight to the Auth0 login — never the app
    // (except the anonymous /public/* routes, which a logged-out client must reach).
    // item 984 — NEVER auto-redirect a DENIED login (?error=access_denied bounced back,
    // e.g. the invitation-only tenant rule): Auth0 silently reuses the denied session
    // and denies again, so the redirect loops forever. The failed-login screen below
    // owns that state instead.
    if (!isPublicRoute && shouldRedirectToLogin(isLoading, isAuthenticated, Boolean(authError))) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, isPublicRoute, authError]);

  // item 455 — the tool/settings surfaces (layer 2). ONE list, spread into BOTH
  // switches: the canvas switch (so direct loads and layer-disabled seats still
  // resolve) and the slide-up panel's switch. React flattens the array children,
  // so Switch matching works normally.
  const layer2Routes = [
    <Route path="/settings" key="settings"><SettingsPage /></Route>,
    <Route path="/reports" key="reports"><ReportsPage /></Route>,
    <Route path="/chat" key="chat"><ChatFocusPage /></Route>,
    <Route path="/mailbox" key="mailbox"><MailboxPage /></Route>,
    <Route path="/accountant" key="accountant"><AccountantPage /></Route>,
    // item 1511 — the retired Timesheet + IC-invoice tools (items 1194/1197 moved their sections
    // to My Book → My Pay+) now REDIRECT there; the two prefixes stay in LAYER2_PREFIXES so the
    // canvas anchor underneath is never polluted by a /timesheet deep link.
    <Route path="/timesheet" key="timesheet"><Redirect to="/my-book?tab=my-pay" /></Route>,
    <Route path="/ic-invoices" key="ic-invoices"><Redirect to="/my-book?tab=my-pay" /></Route>,
    <Route path="/department-tasks" key="department-tasks"><DepartmentTasksPage /></Route>, // item 274
    <Route path="/appearance" key="appearance"><AppearancePage /></Route>,
    <Route path="/users" key="users"><UsersPage /></Route>,
    // item 557 — entity form routes key the form on :id too (same non-remount leak as the
    // document forms: keepDirtyOnReinitialize + AutoSaveField refs carried across records)
    <Route path="/user/:id" key="user" render={({ match }) => <UserForm key={match.params.id} />} />,
    <Route path="/items" key="items"><ItemsPage /></Route>,
    <Route path="/item/:id" key="item" render={({ match }) => <ItemForm key={match.params.id} />} />,
    <Route path="/customers" key="customers"><ContactPage /></Route>,
    <Route path="/customer/:id" key="customer" render={({ match }) => <ContactForm key={match.params.id} />} />,
    <Route path="/my-book" key="my-book"><MyBookOfBusinessPage /></Route>, // item 265
    <Route path="/my-time" key="my-time"><MyTimePage /></Route>, // item 1337b — the work-order timer home
    <Route path="/dashboard/live" key="dashboard-live"><LiveDashboardPage /></Route>, // item 326
    <Route path="/sales-bible" key="sales-bible"><SalesBibleFocusPage /></Route>, // item 476 — panel view
    <Route path="/rnwy-fleet" key="rnwy-fleet"><RnwyFleetPage /></Route>, // item 861 — live capacity fleet
    <Route path="/customer-requests" key="customer-requests"><CustomerRequestsPage /></Route>, // item 868a
  ];

  // item 594 — an Executive never renders the FINANCIAL layer-2 panels (Accountant, Reports).
  // Dropping them here (spread into BOTH switches) means a typed /accountant or /reports URL
  // falls through to the canvas guard's redirect rather than opening the panel.
  const visibleLayer2Routes = isExecutive
    ? layer2Routes.filter((r) => r.key !== 'accountant' && r.key !== 'reports')
    : layer2Routes;

  // item 227 — render the public proposal view with only ChakraProvider (no app shell,
  // no brand/appearance/react-query providers, no auth), regardless of session state.
  if (isPublicRoute) {
    return (
      <ChakraProvider theme={basixTheme}>
        {/* item 1515 — each public page is its own chunk */}
        <Suspense fallback={<RouteFallback fullScreen />}>
          <Switch>
            <Route path="/public/proposal/:companyId/:token" component={PublicProposalPage} />
            <Route path="/public/eeula/:companyId/:token" component={PublicEeulaPage} />
            <Route path="/public/pay/:companyId/:outcome" component={PublicPaymentPage} />
            {/* item 882 — the durable self-serve auto-pay link from every monthly invoice email */}
            <Route path="/public/autopay/:companyId/:token" component={PublicAutoPayPage} />
            <Route path="/public/collections/:companyId/:token" component={PublicCollectionsPage} />
          </Switch>
        </Suspense>
      </ChakraProvider>
    );
  }

  // item 984 — FAILED LOGIN screen (Chad: "it should say failed login"). A login Auth0
  // denied (invitation-only rule, blocked account, wrong social account) lands here with
  // the reason, instead of the app auto-retrying into an endless spin/flash. "Try again"
  // clears the sticky Auth0 session first so the next attempt is a genuinely fresh login
  // (otherwise Auth0 silently re-picks the same denied account).
  if (shouldShowLoginFailed(isLoading, isAuthenticated, Boolean(authError))) {
    return (
      <ChakraProvider theme={basixTheme}>
        <Center h="100vh" px={6}>
          <VStack spacing={4} maxW="480px" textAlign="center">
            <Heading size="lg" color="red.400">Login failed</Heading>
            {/* item 958 — light-mode contrast: this screen sits outside the app providers, so the
                light-mode ink comes from Chakra's body class rather than a hook (dark unchanged) */}
            <Text color="whiteAlpha.800" sx={{ '.chakra-ui-light &': { color: 'gray.700' } }}>
              {authError?.message || 'Your sign-in was not accepted.'}
            </Text>
            <Text fontSize="sm" color="whiteAlpha.600" sx={{ '.chakra-ui-light &': { color: 'gray.500' } }}>
              {/* item 1077 (Chad) — the #1 real-world cause: Google-joined teammates
                  typing a password. Say it here too, not just on the Auth0 side. */}
              If you joined Basix with Google, use “Continue with Google” — Google accounts
              have no password here. Basix logins are invite-based — sign in with the same
              email address your invitation was sent to. If this keeps happening, contact
              your administrator.
            </Text>
            <Button
              colorScheme="green"
              onClick={() => logout({ returnTo: window.location.origin })}
            >
              Try again
            </Button>
          </VStack>
        </Center>
      </ChakraProvider>
    );
  }

  // The entire shell is auth-gated: until Auth0 confirms a session, render a
  // neutral loading screen instead of flashing the app chrome (item 37).
  if (!shouldRenderApp(isLoading, isAuthenticated)) {
    return (
      <ChakraProvider theme={basixTheme}>
        <Center h="100vh">
          <Spinner size="xl" color="brandGreen.500" thickness="4px" label="Signing you in" />
        </Center>
      </ChakraProvider>
    );
  }

  // item 1014 (Chad: "it let him in but with access to nothing — that is scary") — FAIL CLOSED:
  // an authenticated session that can't resolve to a Basix user (token missing the binding
  // claim — e.g. an unlinked Google account — or the self-record fetch rejected) gets a hard
  // stop with an explanation, never an empty shell.
  if (sessionBroken) {
    // item 1084 (Chad, trapped on this screen) — a STALE impersonation (per-tab
    // sessionStorage from an earlier test) can make the TARGET's record fetch fail,
    // which reads as "broken session" — but the exit banner never mounts here, so
    // there was no way out short of a new tab. Self-heal: drop the impersonation and
    // reload as yourself instead of dead-ending.
    if (getImpersonatedUserId() || getImpersonatedRoleName()) {
      stopImpersonation();
      return null; // stopImpersonation navigates; render nothing meanwhile
    }
    return (
      <ChakraProvider theme={basixTheme}>
        <Center h="100vh" px={6}>
          <VStack spacing={4} maxW="520px" textAlign="center">
            <Heading size="lg" color="red.400">This login isn&apos;t connected to Basix</Heading>
            <Text>
              {`You're signed in${auth0User?.email ? ` as ${auth0User.email}` : ''}, but this sign-in
              method isn't linked to a Basix account, so nothing can load.`}
            </Text>
            <Text fontSize="sm" color="gray.500">
              This usually happens when a Google sign-in created a separate, unlinked login.
              Contact your administrator to link it — or sign out and use the email + password
              from your invitation.
            </Text>
            <Button colorScheme="green" onClick={() => logout({ returnTo: window.location.origin })}>
              Sign out
            </Button>
          </VStack>
        </Center>
      </ChakraProvider>
    );
  }

  // item 859 — hold the shell until the ROLE resolves: rendering the internal chrome while the
  // user record was still in flight briefly mounted the sidebar + NotificationBell for EXTERNAL
  // seats — long enough to fire the chat "New message" toast at an Outside Sales rep (Chad's
  // screenshot) and flash internal UI at portal users. Same neutral screen as the auth gate.
  if (roleLoading) {
    return (
      <ChakraProvider theme={basixTheme}>
        <Center h="100vh">
          <Spinner size="xl" color="brandGreen.500" thickness="4px" label="Loading your workspace" />
        </Center>
      </ChakraProvider>
    );
  }

  // item 843 — an external Customer Builder gets ONLY the stripped portal: none of the internal
  // chrome (sidebar, KPIs, command palette, financials, pin-lock, project mode) ever mounts. The
  // backend is the real boundary; this short-circuit keeps the whole internal UI out of their reach.
  if (isCustomerBuilder) {
    return (
      <BrandSourceProvider>
        <SkinProvider>
          {/* item 852 — the portal wears its OWN brand (RNWY kit by default), not the tenant's */}
          <PortalThemeProvider>
            <AppearanceProvider>
              <ConfirmProvider>
                <NewVersionWatcher />
                <MaintenanceBanner />
                {/* item 1515 — the portal is its own chunk; the gates above stay outside Suspense */}
                <Suspense fallback={<RouteFallback fullScreen />}>
                  <CustomerPortalApp />
                </Suspense>
              </ConfirmProvider>
            </AppearanceProvider>
          </PortalThemeProvider>
        </SkinProvider>
      </BrandSourceProvider>
    );
  }

  // item 855 — an Outside Sales (Sales I/O) rep gets the SAME stripped treatment as the
  // Customer Builder: a clean portal shell (no sidebar, no Appearance page, no Settings),
  // FORCED into the company (Pulse Group) brand, with only the color-mode menu. The old
  // in-shell route guards below (item 272) stay as belt-and-braces but are now unreachable.
  if (isOutsideSales) {
    return (
      <BrandSourceProvider>
        <SkinProvider>
          <OutsideSalesThemeProvider>
            <AppearanceProvider>
              <ConfirmProvider>
                <NewVersionWatcher />
                <MaintenanceBanner />
                <Suspense fallback={<RouteFallback fullScreen />}>
                  <OutsideSalesPortalApp />
                </Suspense>
              </ConfirmProvider>
            </AppearanceProvider>
          </OutsideSalesThemeProvider>
        </SkinProvider>
      </BrandSourceProvider>
    );
  }

  return (
    <BrandSourceProvider>
    <SkinProvider>
    <BrandThemeProvider>
      <AppearanceProvider>
        <PinLockProvider>
          <ProjectModeProvider>
          <ConfirmProvider>
          <Box>
            {/* item 1502 — "a new version is live" toast, once per tab, never auto-reloads */}
            <NewVersionWatcher />
            <MaintenanceBanner />
            <FirstLoginBrandWizard />
            <CommandPalette />
            <KeyboardShortcuts />
            <BreadcrumbsProvider>
            <DashboardExpandProvider>
            <SidebarWithHeader
              /* item 361 — THE PERMANENT sticky fix: the KPI stack renders in the shell's
                 HEADER SLOT, physically OUTSIDE the scroll region. Content can never
                 scroll under or over it, and page titles pin against the scrollport with
                 a CONSTANT offset — no measured CSS var to drift (the root cause of the
                 313/353/353b regressions). */
              header={liveDashFocus ? null : (
                <>
                  {/* item 1063 — the spacer must track TopBar's OWN breakpoint (lg): the old
                      xl cutoff left 72px of dead space at 992–1279px (iPad landscape) */}
                  <Box h="72px" display={{ lg: 'none' }} />
                  <Box pt={{ base: 2, md: 3 }} bg="appHeaderBacking">
                    <ErrorBoundary resetKey={pathname}>
                      <KpiStrip />
                    </ErrorBoundary>
                    <ErrorBoundary resetKey={pathname}>
                      <ProjectContextBar />
                    </ErrorBoundary>
                    {/* item 456 — the breadcrumb trail lives in the STICKY header
                        (registered by pages via FormBreadcrumbs), pinned for the
                        CANVAS route so it never scrolls away */}
                    <ErrorBoundary resetKey={canvasLocation.pathname}>
                      <HeaderBreadcrumbs path={canvasLocation.pathname} />
                    </ErrorBoundary>
                  </Box>
                </>
              )}
            >
              {/* item 258 — the expanded dashboard bento lives at the TOP of the scroll
                  region, so expanding it pushes the page down and scrolls as one document. */}
              {!liveDashFocus && (
              <ErrorBoundary resetKey={pathname}>
                <DashboardBentoSlot />
              </ErrorBoundary>
              )}
              {/* item 326 — ambient data pulse keeps the always-visible KPI strip live
                  everywhere; the /dashboard/live page runs its own faster one. 60s
                  (item 330): each tick refetches five full lists app-wide, and 30s was
                  real churn on slower PCs — focus/navigation refetches cover the gaps. */}
              {!liveDashFocus && <LiveDataPulse intervalMs={60000} />}
              <ErrorBoundary resetKey={pathname}>
                <Box>
                  {/* item 455 — the CANVAS switch renders the anchored layer-0 location
                      while a layer-2 panel is open (canvasLocation), so the page under
                      the panel never unmounts and your place survives. */}
                  {/* item 1515 — lazy route chunks resolve under this boundary (gates stay outside) */}
                  <Suspense fallback={<RouteFallback />}>
                  <Switch location={canvasLocation as any}>
                    {/* item 272 — Outside Sales portal guard: only My Book + Appearance,
                        everything else (incl. the catch-all dashboard) redirects to My Book.
                        Placed first so Switch matches these before the company routes below. */}
                    {isOutsideSales && (
                      <Route path="/my-book">
                        <MyBookOfBusinessPage />
                      </Route>
                    )}
                    {isOutsideSales && (
                      <Route path="/appearance">
                        <AppearancePage />
                      </Route>
                    )}
                    {isOutsideSales && <Redirect to="/my-book" />}
                    {/* item 594 — Executive money-surface guard: every financial route
                        (accountant, reports, invoices, bills, POs, subscriptions) redirects to
                        the dashboard. Placed before the routes it guards so Switch matches it
                        first; the nav already hides these, this stops a hand-typed URL. */}
                    {isExecutive && (
                      <Route path={['/accountant', '/reports', '/invoices', '/invoice', '/bills', '/bill', '/PurchaseOrder', '/recurring']}>
                        <Redirect to="/" />
                      </Route>
                    )}
                    {/* item 457 — subscriptions are Basix-core (under Invoices), so
                        /recurring stays a canvas route, not a layer-2 panel */}
                    <Route path="/recurring">
                      <RecurringRevenuePage />
                    </Route>
                    {/* item 453 — standalone Commissions retired; everything commission
                        lives under My Book now. Redirect old links (query preserved so a
                        /commissions?proposal=X deep-link still lands scoped). */}
                    <Route
                      path="/commissions"
                      render={({ location: loc }) => (
                        <Redirect to={{ pathname: '/my-book', search: loc.search }} />
                      )}
                    />
                    {visibleLayer2Routes}
                    <Route path="/proposals">
                      <ProposalsPage />
                    </Route>
                    {/* item 471 — all hand-offs list (Basix Six ▸ Proposals ▸ Hand-offs) */}
                    <Route path="/handoffs">
                      <HandOffsPage />
                    </Route>
                    {/* item 557 — every form route keys its form on the :id param. React Router
                        v5 keeps the same element mounted when only the param changes, so WITHOUT
                        the key, switching /proposal/A → /proposal/B carried A's form instance —
                        dirty fields (keepDirtyOnReinitialize), the items grid, save badge, and
                        auto-save baselines — into B, and auto-save could write A's edits onto B.
                        The key remounts a fresh, isolated form per document. */}
                    {/* the key includes the query string too: two back-to-back NEW docs share
                        the NIL id but differ on ?projectId= — they must not share a form. */}
                    <Route path="/proposal/:id" render={({ match, location: l }) => <ProposalForm key={match.params.id + l.search} />} />
                    <Route path="/PurchaseOrder/:id" render={({ match, location: l }) => <PurchaseOrderForm key={match.params.id + l.search} />} />
                    <Route path="/PurchaseOrder">
                      <PurchaseOrdersPage />
                    </Route>
                    <Route path="/WorkOrder/:id" render={({ match, location: l }) => <WorkOrderForm key={match.params.id + l.search} />} />
                    <Route path="/WorkOrders">
                      <WorkOrdersPage />
                    </Route>
                    <Route path="/Invoices">
                      <InvoicePage />
                    </Route>
                    <Route path="/invoice/:id" render={({ match, location: l }) => <InvoiceForm key={match.params.id + l.search} />} />
                    <Route path="/bills">
                      <BillPage />
                    </Route>
                    <Route path="/bill/:id" render={({ match, location: l }) => <BillForm key={match.params.id + l.search} />} />
                    <Route path="/project/edit/:id" render={({ match }) => <ProjectForm key={match.params.id} />} />
                    <Route path="/project/:id">
                      <ProjectPage />
                    </Route>
                    {/* item 476 — Sales Bible moved to a layer-2 panel view (in layer2Routes) */}
                    <Route path="/">
                      <DashboardPage />
                    </Route>
                  </Switch>
                  </Suspense>
                </Box>
              </ErrorBoundary>
            </SidebarWithHeader>
            {/* item 455 — LAYER 2: the tool panel slides up over the live canvas.
                Close (swipe-down / Esc / X) returns to the anchored canvas route. */}
            <AnimatePresence>
              {panelOpen && (
                <LayerPanel
                  key="layer2-panel"
                  headerPath={pathname}
                  onClose={() => history.push({
                    pathname: lastCanvasLocRef.current.pathname,
                    search: lastCanvasLocRef.current.search,
                  })}
                >
                  <ErrorBoundary resetKey={pathname}>
                    {/* item 1515 — every tool panel is a lazy chunk */}
                    <Suspense fallback={<RouteFallback />}>
                      <Switch location={location}>
                        {visibleLayer2Routes}
                      </Switch>
                    </Suspense>
                  </ErrorBoundary>
                </LayerPanel>
              )}
            </AnimatePresence>
            </DashboardExpandProvider>
            </BreadcrumbsProvider>
          </Box>
          </ConfirmProvider>
          </ProjectModeProvider>
          {/* Personal PIN-code idle lock (item 106) — a fixed full-screen overlay
            mounted OUTSIDE the routed content; freezes the whole shell when idle
            while the Auth0 session stays alive. */}
          <PinLockScreen />
          {/* item 123: in-app guided product tour — auto-shows once, replayable from Settings */}
          <GuidedTour />
          {/* item 323 — fixed "VIEWING AS role — EXIT" pill while impersonating */}
          <ImpersonationBanner />
          {/* item 327 — quiet "STEALTH" pill while every number is masked */}
          <StealthIndicator />
        </PinLockProvider>
      </AppearanceProvider>
    </BrandThemeProvider>
    </SkinProvider>
    </BrandSourceProvider>
  );
}

export default App;
